import { inject, Injectable } from '@angular/core';
import { CreateWorkContextParams, UpdateWorkContextParams, WorkContext } from '../models/work-context.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';

/**
 * Work contexts (`work-context.manage`) — saved "Campaign" presets a
 * canvasser activates once per shift (design doc §6.5). Which one is
 * currently active is a per-device preference (see MyDayComponent /
 * UserStorageService), not stored here.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkContextService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);

  /** Active, non-deleted work contexts, alphabetical. */
  async getActive(): Promise<WorkContext[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM work_contexts WHERE is_active = 1 AND deleted_at IS NULL ORDER BY name',
    );
    return rows as WorkContext[];
  }

  /** Every non-deleted work context, active or not — for admin management. */
  async getAll(): Promise<WorkContext[]> {
    await this.db.init();
    const rows = await this.db.exec('SELECT * FROM work_contexts WHERE deleted_at IS NULL ORDER BY name');
    return rows as WorkContext[];
  }

  createActiveQuery(): QuerySignal<WorkContext[]> {
    return this.db.createQuery('work_contexts', () => this.getActive());
  }

  createQuery(): QuerySignal<WorkContext[]> {
    return this.db.createQuery('work_contexts', () => this.getAll());
  }

  async create(params: CreateWorkContextParams): Promise<WorkContext> {
    const userId = this.requireUser('create');
    await this.db.init();
    const id = this.db.generateId();
    const now = this.clockSync.now();

    await this.db.run(
      `INSERT INTO work_contexts
         (id, name, description, default_tag_ids, default_interaction_type, starts_at, ends_at,
          created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        params.name,
        params.description ?? '',
        JSON.stringify(params.default_tag_ids ?? []),
        params.default_interaction_type ?? '',
        params.starts_at ?? null,
        params.ends_at ?? null,
        userId,
        now,
        userId,
        now,
      ],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve work context after creation');
    return created;
  }

  async update(params: UpdateWorkContextParams): Promise<WorkContext> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) throw new Error(`Work context '${params.id}' not found`);

    const next: WorkContext = {
      ...existing,
      name: params.name ?? existing.name,
      description: params.description ?? existing.description,
      default_tag_ids: params.default_tag_ids ? JSON.stringify(params.default_tag_ids) : existing.default_tag_ids,
      default_interaction_type: params.default_interaction_type ?? existing.default_interaction_type,
      starts_at: params.starts_at !== undefined ? params.starts_at : existing.starts_at,
      ends_at: params.ends_at !== undefined ? params.ends_at : existing.ends_at,
      is_active: params.is_active ?? existing.is_active,
    };

    await this.db.run(
      `UPDATE work_contexts SET name = ?, description = ?, default_tag_ids = ?, default_interaction_type = ?,
         starts_at = ?, ends_at = ?, is_active = ?, updated_by = ?, updated_at = ?
       WHERE id = ?`,
      [
        next.name,
        next.description,
        next.default_tag_ids,
        next.default_interaction_type,
        next.starts_at,
        next.ends_at,
        next.is_active,
        userId,
        this.clockSync.now(),
        params.id,
      ],
    );

    return { ...next, updated_by: userId };
  }

  private async getById(id: string): Promise<WorkContext | null> {
    const rows = await this.db.exec('SELECT * FROM work_contexts WHERE id = ? AND deleted_at IS NULL', [id]);
    return rows.length > 0 ? (rows[0] as WorkContext) : null;
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error(`User must be authenticated to ${action} a work context`);
    return userId;
  }
}
